import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext } from 'react-hook-form'
import type { SelectOption } from '@/components/molecules'
import { useRentersPolicy } from '@/shared/hooks/queries/use-renters-policy'
import { normalizePhone } from '@/shared/helpers/normalize-phone'
import { ACTIVE_POLICY_STATUS, US_PHONE_REGEX } from '@/shared/constants/claims'
import type { RentersClaimFormValues } from '../page.claims.renters.hook'

export function useClaimsRentersStepOne() {
  const { t } = useTranslation()
  const {
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useFormContext<RentersClaimFormValues>()

  const { data: policies = [] } = useRentersPolicy()

  const policyOptions = useMemo<SelectOption[]>(
    () =>
      policies
        .filter(
          (policy) =>
            policy.status === ACTIVE_POLICY_STATUS &&
            policy.policyExternalId !== undefined,
        )
        .map((policy) => ({
          value: String(policy.policyExternalId),
          label: policy.address?.address
            ? `${policy.address.address}, ${policy.address.city ?? ''}`
            : `#${policy.policyExternalId}`,
        })),
    [policies],
  )

  useEffect(() => {
    const [onlyPolicy] = policyOptions
    if (policyOptions.length === 1 && onlyPolicy && !getValues('policyId')) {
      setValue('policyId', onlyPolicy.value)
    }
  }, [policyOptions, setValue, getValues])

  const requiredRule = { required: t('common.required') }
  const rules = {
    policyId: requiredRule,
    dateOfLoss: requiredRule,
    description: requiredRule,
    phone: {
      ...requiredRule,
      validate: (value: string) =>
        US_PHONE_REGEX.test(normalizePhone(value)) ||
        t('claims.renters.form.phoneError'),
    },
  }

  const fieldErrors = {
    policyId: errors.policyId?.message,
    dateOfLoss: errors.dateOfLoss?.message,
    description: errors.description?.message,
    phone: errors.phone?.message,
  }

  function sanitizePhone(value: string): string {
    return value.replace(/[^\d+()\s-]/g, '')
  }

  return {
    control,
    rules,
    fieldErrors,
    policyOptions,
    sanitizePhone,
    maxDateOfLoss: new Date(),
  }
}
